import { Major, MajorResponse, MajorSearchResult } from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';
import { selectIsSharedSessionActive } from '../sharedSession/sharedSession';
import type { RootState } from '../store';

export const majorApi = createApi({
  reducerPath: 'majorApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getMajorById: build.query<Major, string>({
      query: (id) => `majors/${id}`,
      keepUnusedDataFor: 60 * 60 * 24,
      transformResponse: (response: MajorResponse): Major => ({
        id: response.id,
        name: response.name,
        media_url: response.media_url,
        category: response.category,
        description: response.description,
        typicalCollege: response.typical_college,
        demand: response.demand,
        optionValueScore: response.option_value_score,
        optionValueName: response.option_value_name,
        underEmployment: response.underemployment,
        underEmploymentRationale: response.underemployment_rationale,
        competitiveness: response.competitiveness,
        workload: response.workload,
        gradSchoolNecessity: response.grad_school_necessity,
        gradSchoolNecessityRationale: response.grad_school_necessity_rationale,
        code: response.code,
        rigor: response.rigor,
        specialization: response.specialization,
        careerPathRelevance: response.careerPathRelevance.map((relevance) => ({
          id: relevance.id,
          academicMajorsId: relevance.academic_majors_id,
          careerPathId: relevance.career_path_id,
          relevance: relevance.relevance,
          rationale: relevance.rationale,
        })),
        sampleJobs: response.sample_jobs,
        medianWageEarlyCareer: response.median_wage_early_career,
        medianWageMidCareer: response.median_wage_mid_career,
      }),
    }),
    getMajorByCode: build.query<Major, string>({
      queryFn: async (code, queryApi, _extraOptions, baseQuery): Promise<{ data: Major }> => {
        const state = queryApi.getState() as RootState;
        const isSharedSession: boolean = selectIsSharedSessionActive(state);

        const url: string = isSharedSession
          ? `shared/solution-session/majors/code/${code}`
          : `majors/code/${code}`;

        return baseQuery({ url }) as Promise<{ data: Major }>;
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getMajorsByIds: build.query<Major[], number[]>({
      queryFn: async (ids, queryApi, _extraOptions, baseQuery): Promise<{ data: Major[] }> => {
        const state = queryApi.getState() as RootState;
        const isSharedSession: boolean = selectIsSharedSessionActive(state);

        const url: string = isSharedSession
          ? `shared/solution-session/majors?ids=${ids.join(',')}`
          : `majors?ids=${ids.join(',')}`;

        return baseQuery({ url }) as Promise<{ data: Major[] }>;
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getAllMajors: build.query<MajorSearchResult[], undefined>({
      query: () => 'majors',
      keepUnusedDataFor: 60 * 60 * 24,
    }),
  }),
});

export const {
  useGetMajorByIdQuery,
  useGetMajorByCodeQuery,
  useGetMajorsByIdsQuery,
  useGetAllMajorsQuery,
  usePrefetch: usePrefetchMajor,
  util: { resetApiState },
} = majorApi;
